import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isAuthPage = pathname === '/signup' || pathname === '/signin';
  const isHomePage = pathname === '/';
  const useDarkText = scrolled || isAuthPage || isHomePage;

  const linkCls = (path) =>
    `text-sm font-medium transition-colors ${
      pathname === path 
        ? (useDarkText ? "text-t1" : "text-white") 
        : (useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white")
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-ft backdrop-blur-xl border-b border-white/[0.07] py-3 shadow-lg"
          : "bg-ft py-5"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center gap-10">
        {/* Logo */}
        <Link to="/" className={`flex items-center gap-2 font-display font-bold text-xl tracking-tight shrink-0 ${useDarkText ? "text-t1" : "text-white"}`}>
          <span className="text-accent text-sm">✦</span>
          RoomViz<span className="text-accent2">AI</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8 flex-1">
          <Link to="/" className={linkCls("/")}>Home</Link>
          {/* <Link to="/visualizer" className={linkCls("/visualizer")}>Visualizer</Link>
          <a href="#features" className={`text-sm font-medium transition-colors ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}>Features</a>
          <a href="#how-it-works" className={`text-sm font-medium transition-colors ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}>How it works</a> */}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3 ml-auto">
          <div className="relative">
            <button
              onClick={() => setLoginDropdownOpen(!loginDropdownOpen)}
              className={`text-sm font-medium transition-colors px-4 py-2 rounded-lg flex items-center gap-2 ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}
            >
              Sign In
              <svg className={`w-3 h-3 transition-transform ${loginDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {loginDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-surface border border-white/[0.1] rounded-xl shadow-xl overflow-hidden z-50">
                <Link
                  to="/signin"
                  onClick={() => setLoginDropdownOpen(false)}
                  className="block px-4 py-3 text-sm text-t2 hover:text-t1 hover:bg-white/[0.05] transition-colors"
                >
                  User Login
                </Link>
                <Link
                  to="/retailer/login"
                  onClick={() => setLoginDropdownOpen(false)}
                  className="block px-4 py-3 text-sm text-t2 hover:text-t1 hover:bg-white/[0.05] transition-colors border-t border-white/[0.06]"
                >
                  Retailer Login
                </Link>
              </div>
            )}
          </div>
          {/* Signup is disabled until signup type selection is added. */}
          {/* <Link
            to="/signup"
            className="text-sm font-semibold bg-accent hover:bg-accent2 text-white px-5 py-2 rounded-lg transition-all duration-200 hover:-translate-y-px hover:shadow-[0_0_20px_rgba(124,92,252,0.4)]"
          >
            Get Started
          </Link> */}
        </div>

        {/* Burger */}
        <button
          className={`md:hidden ml-auto flex flex-col gap-1.5 p-1 ${useDarkText ? "" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          <span className={`block w-5 h-0.5 rounded transition-all ${useDarkText ? "bg-t1" : "bg-white"}`} />
          <span className={`block w-5 h-0.5 rounded transition-all ${useDarkText ? "bg-t1" : "bg-white"}`} />
          <span className={`block w-5 h-0.5 rounded transition-all ${useDarkText ? "bg-t1" : "bg-white"}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className={`md:hidden border-t border-white/[0.07] px-6 py-5 flex flex-col gap-5 ${useDarkText ? "bg-bg2" : "bg-bg/95 backdrop-blur-xl"}`}>
          <Link to="/" className={linkCls("/")}>Home</Link>
          <Link to="/visualizer" className={linkCls("/visualizer")}>Visualizer</Link>
          <a href="#features" className={`text-sm font-medium ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}>Features</a>
          <a href="#how-it-works" className={`text-sm font-medium ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}>How it works</a>
          <div className="flex gap-3 pt-2 border-t border-white/[0.07]">
            <Link to="/signin" className={`text-sm font-medium ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}>User Login</Link>
            <Link to="/retailer/login" className={`text-sm font-medium ${useDarkText ? "text-t2 hover:text-t1" : "text-white/80 hover:text-white"}`}>Retailer Login</Link>
            {/* Signup is disabled until signup type selection is added. */}
            {/* <Link to="/signup" className="text-sm font-semibold bg-accent text-white px-4 py-2 rounded-lg">Get Started</Link> */}
          </div>
        </div>
      )}
    </nav>
  );
}
